"use client";

import { Button } from "components/ui/Button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "components/ui/Command";
import { Drawer, DrawerContent, DrawerTrigger } from "components/ui/Drawer";
import { Separator } from "components/ui/Separator";
import classNames from "components/ui/classNames";
import { FormattedMessage, useIntl } from "react-intl";
import { useMediaQuery } from "usehooks-ts";

export type ComboBoxOptionItem = {
  value: (string | number | boolean) | (string | number | boolean)[];
  label: string;
  count: number;
};

interface ResponsiveComboBoxProps {
  isOpen?: boolean;
  options: ComboBoxOptionItem[];
  value: ComboBoxOptionItem["value"] | null;
  hideCounts?: boolean;
  onChange: (value: ComboBoxOptionItem["value"] | null) => void;
  onOpenChange: (isOpen: boolean) => void;
}
export function ResponsiveComboBox({
  isOpen = false,
  options,
  value,
  hideCounts,
  onChange,
  onOpenChange,
}: ResponsiveComboBoxProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const option = options.find((option) => option.value === value);

  if (isDesktop) {
    return (
      <OptionsList
        value={value}
        options={options}
        hideCounts={hideCounts}
        onOpenChange={onOpenChange}
        setSelectedOption={onChange}
      />
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerTrigger asChild={true}>
        <Button variant="outline" className="w-[150px] justify-start">
          {option ? option.label : <FormattedMessage defaultMessage="Pesquise" id="iKjdaT" />}
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <div className="mt-4 border-t">
          <OptionsList
            value={value}
            options={options}
            hideCounts={hideCounts}
            onOpenChange={onOpenChange}
            setSelectedOption={onChange}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface OptionsListProps {
  value: ComboBoxOptionItem["value"] | null;
  options: ComboBoxOptionItem[];
  hideCounts?: boolean;
  onOpenChange: (open: boolean) => void;
  setSelectedOption: (option: ComboBoxOptionItem["value"] | null) => void;
}
function OptionsList({ value, options, hideCounts, onOpenChange, setSelectedOption }: OptionsListProps) {
  const intl = useIntl();
  const hasCounts = !hideCounts ? options.every((option) => option.count > 0) : false;
  return (
    <Command value={value === null ? undefined : String(value)}>
      <CommandInput placeholder={intl.formatMessage({ defaultMessage: "Pesquise...", id: "lYByRF" })} />
      <Separator />
      <CommandList>
        <CommandEmpty className="p-0">
          <p
            className={classNames(
              "px-4 pt-4 text-center  text-base font-medium tracking-tight text-slate-600 dark:text-slate-400",
            )}
          >
            <FormattedMessage defaultMessage="Nada encontrado..." id="kFeIbv" />
          </p>
        </CommandEmpty>
        <CommandGroup>
          {options
            .filter((option) => {
              return option.value !== "null";
            })
            .map((option) => {
              return {
                ...option,
                isSelected: option.value === value,
              };
            })
            .sort((a, b) => {
              if (options.length <= 6) {
                return 0;
              }
              if (a.isSelected) return -1;
              if (b.isSelected) return 1;
              return 0;
            })
            .map(({ isSelected, ...option }) => {
              return (
                <CommandItem
                  key={option.label}
                  className="group cursor-pointer rounded-lg"
                  value={String(option.value)}
                  data-active={isSelected}
                  onSelect={() => {
                    if (isSelected) {
                      setSelectedOption(null);
                      onOpenChange(false);
                      return;
                    }

                    setSelectedOption(option.value);
                    onOpenChange(false);
                  }}
                >
                  {hasCounts ? (
                    <div className="flex w-full justify-between">
                      <span className="font-medium group-hover:font-bold group-data-[active=true]:font-bold dark:font-bold">
                        {option.label}
                      </span>
                      <strong className="font-bold">{option.count}</strong>
                    </div>
                  ) : (
                    <span className="font-medium group-hover:font-bold group-data-[active=true]:font-bold dark:font-bold">
                      {option.label}
                    </span>
                  )}
                </CommandItem>
              );
            })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
